import { ROOM_TYPES, type RoomType, type RoomsByNight } from "@/lib/utils/accommodations";
import { ANCHORED_ROOM_TYPE, ONE_DAY_MS } from "@/features/event-details/_logic/constants";
import { addDays } from "@/features/event-details/_logic/date-utils";

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;
/** Defensive cap: refuse to render plus 1 year. Guards against corrupt or
 *  malicious `event_dates` triggering an unbounded loop / OOM in the browser. */
const MAX_NIGHTS = 366;

/**
 * Generate the list of nights between two `YYYY-MM-DD` dates, exclusive of
 * the end date. A 3→7 May range yields `["2026-05-03", "2026-05-04", "2026-05-05", "2026-05-06"]`
 * (four nights).
 *
 * Operates in UTC to sidestep DST; the strings are date-only so this is
 * deterministic regardless of viewer timezone. Returns `[]` for spans
 * exceeding `MAX_NIGHTS` so untrusted `event_dates` data can't crash the
 * page with an unbounded loop.
 *
 * @param startDate - Inclusive start, `YYYY-MM-DD`.
 * @param endDate - Exclusive end, `YYYY-MM-DD`.
 * @returns Sorted list of `YYYY-MM-DD` strings, or `[]` for invalid / too-long input.
 */
export function getNightsBetween(startDate: string, endDate: string): string[] {
  if (!YYYY_MM_DD.test(startDate) || !YYYY_MM_DD.test(endDate)) return [];
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  if ((end - start) / ONE_DAY_MS > MAX_NIGHTS) return [];

  const nights: string[] = [];
  for (let t = start; t < end; t += ONE_DAY_MS) {
    nights.push(new Date(t).toISOString().slice(0, 10));
  }
  return nights;
}

/**
 * Pick every valid multi-day range from `events.event_dates` JSONB.
 * Powers the agenda's multi-range pickers — each persisted entry maps
 * to one picker row. Single-day entries (`{date}`) are normalized into
 * a one-day range (start === end) so the agenda can render them as a
 * single Day column. Malformed entries are silently dropped.
 *
 * Order is preserved (consumers may want chronological sort
 * separately).
 *
 * @param eventDates - Raw JSONB value from the events row.
 * @returns Array of `{startDate, endDate}` ranges; empty when nothing usable.
 */
export function pickAllRanges(eventDates: unknown): Array<{ startDate: string; endDate: string }> {
  let parsed = eventDates;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];

  const out: Array<{ startDate: string; endDate: string }> = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as { date?: unknown; startDate?: unknown; endDate?: unknown };
    if (
      typeof e.startDate === "string" &&
      typeof e.endDate === "string" &&
      YYYY_MM_DD.test(e.startDate) &&
      YYYY_MM_DD.test(e.endDate)
    ) {
      if (e.startDate <= e.endDate) out.push({ startDate: e.startDate, endDate: e.endDate });
    } else if (typeof e.date === "string" && YYYY_MM_DD.test(e.date)) {
      out.push({ startDate: e.date, endDate: e.date });
    }
  }
  return out;
}

/**
 * Determine which room-type columns should be visible on initial render.
 * The anchored type (`Single`) is always present; other types appear when
 * any night has a non-zero count for them in the saved data.
 *
 * Used once at mount to seed the session-visible set; thereafter the
 * component manages visibility in state (add via menu, remove via ✕).
 *
 * Accepts both the canonical `RoomsByNight` shape (all seven keys present)
 * and any partial-keyed per-night counts map (e.g. the wizard's draft
 * shape) — callers with optional-count data don't need their own copy.
 *
 * @param accommodations - Per-night room-type counts, canonical or partial.
 * @returns Set of types to render initially.
 */
export function deriveVisibleTypes(accommodations: Record<string, Partial<Record<RoomType, number>>>): Set<RoomType> {
  const visible = new Set<RoomType>([ANCHORED_ROOM_TYPE]);
  for (const counts of Object.values(accommodations)) {
    for (const type of ROOM_TYPES) {
      if ((counts[type] ?? 0) > 0) visible.add(type);
    }
  }
  return visible;
}

/**
 * Total rooms held on one night, across every room type.
 *
 * The single definition of "how many rooms is this night" — the room-block card footers, the
 * summary below, and the stale-mention check all read it, so adding a room type can't leave one
 * of them counting differently from the others.
 *
 * @param counts - One night's room-type counts, canonical or partial.
 * @returns The night's total.
 */
export function sumNightCounts(counts: Partial<Record<RoomType, number>> | undefined): number {
  if (!counts) return 0;
  return ROOM_TYPES.reduce((sum, type) => sum + (counts[type] ?? 0), 0);
}

/**
 * Every night's room total, in date order — the input to "does the block hold N rooms on any
 * night?" checks that a single peak figure can't answer for a ramped block.
 *
 * @param accommodations - Per-night room-type counts, canonical or partial.
 * @returns One total per night present in the map.
 */
export function deriveNightlyRoomTotals(accommodations: Record<string, Partial<Record<RoomType, number>>>): number[] {
  return Object.keys(accommodations)
    .sort()
    .map((date) => sumNightCounts(accommodations[date]));
}

/**
 * Peak (busiest night's) room count and total room-nights across a set of nights — the summary
 * shown in every room-block card's footer ("N peak / M room nights"). Shared by the event-details
 * Rooms section and the create-event wizard's guestrooms grid so the two can't drift on the math.
 *
 * @param nights - The nights to sum over (`YYYY-MM-DD`).
 * @param countsByDate - Per-night room-type counts, canonical or partial.
 * @returns `peakRooms` (max single-night total) and `totalRoomNights` (sum across all nights).
 */
export function computeRoomNightsSummary(
  nights: string[],
  countsByDate: Record<string, Partial<Record<RoomType, number>>>
): { peakRooms: number; totalRoomNights: number } {
  let peakRooms = 0;
  let totalRoomNights = 0;
  for (const date of nights) {
    const nightSum = sumNightCounts(countsByDate[date]);
    if (nightSum > peakRooms) peakRooms = nightSum;
    totalRoomNights += nightSum;
  }
  return { peakRooms, totalRoomNights };
}

/**
 * Group the dates in `accommodations` into contiguous ranges. A range is
 * `{ startDate, endDate }` where `startDate` is the first night and
 * `endDate` is the checkout day (last night + 1, matching the
 * `getNightsBetween` convention). Used by the Rooms section's
 * range-picker UI to show one picker per booking block.
 *
 * Dates with malformed keys are silently ignored.
 *
 * @param accommodations - Canonical normalized accommodations map.
 * @returns Sorted list of contiguous ranges; empty when no nights.
 */
export function deriveContiguousRanges(accommodations: RoomsByNight): Array<{ startDate: string; endDate: string }> {
  const dates = Object.keys(accommodations)
    .filter((d) => YYYY_MM_DD.test(d))
    .sort();
  if (dates.length === 0) return [];

  const ranges: Array<{ startDate: string; endDate: string }> = [];
  let chunkStart = dates[0];
  let prev = dates[0];
  for (let i = 1; i < dates.length; i++) {
    const curr = dates[i];
    const expectedNext = addDays(prev, 1) ?? prev;
    if (curr === expectedNext) {
      prev = curr;
    } else {
      ranges.push({ startDate: chunkStart, endDate: addDays(prev, 1) ?? prev });
      chunkStart = curr;
      prev = curr;
    }
  }
  ranges.push({ startDate: chunkStart, endDate: addDays(prev, 1) ?? prev });
  return ranges;
}

/**
 * Union a list of (possibly overlapping or out-of-order) `{startDate,endDate}`
 * ranges into a sorted, non-overlapping set. Two ranges merge when they overlap
 * OR are adjacent/touching — `gapDays = (nextStart - prevEnd)/ONE_DAY_MS <= 1` —
 * extending the kept range's `endDate` to the max of the two. Ranges separated
 * by a real gap (> 1 day) stay separate, so calendar gaps survive.
 *
 * Note: a genuine 1-day gap between two blocks collapses into one range (the
 * `<= 1` rule). This matches the helper removed in PR #1521 and is intended — a
 * guestroom checkout day touching a meeting start day must merge.
 *
 * Malformed entries (bad format or start > end) are dropped. Recreated from the
 * helper deleted in #1521; `event_dates` is now derived via {@link deriveEventDates}.
 *
 * @param ranges - Ranges in any order; each `{startDate,endDate}` as `YYYY-MM-DD`.
 * @returns Sorted, merged ranges; empty when nothing usable.
 */
export function mergeRanges(
  ranges: Array<{ startDate: string; endDate: string }>
): Array<{ startDate: string; endDate: string }> {
  const valid = ranges.filter(
    (r) =>
      r &&
      typeof r.startDate === "string" &&
      typeof r.endDate === "string" &&
      YYYY_MM_DD.test(r.startDate) &&
      YYYY_MM_DD.test(r.endDate) &&
      r.startDate <= r.endDate
  );
  if (valid.length === 0) return [];
  const sorted = [...valid].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const out: Array<{ startDate: string; endDate: string }> = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i];
    const last = out[out.length - 1];
    const lastEndMs = Date.parse(`${last.endDate}T00:00:00Z`);
    const currStartMs = Date.parse(`${curr.startDate}T00:00:00Z`);
    const gapDays = (currStartMs - lastEndMs) / ONE_DAY_MS;
    // Overlap or touch (gap of at most one day) → extend the kept range's end.
    if (gapDays <= 1) {
      if (curr.endDate > last.endDate) last.endDate = curr.endDate;
    } else {
      out.push({ ...curr });
    }
  }
  return out;
}

/**
 * Derive `events.event_dates` from its two owning columns: the union of the
 * guestroom-derived ranges (from `accommodations`, whose `endDate` is the
 * checkout day = last night + 1) and the agenda's `meeting_room_dates`
 * (inclusive ranges). Overlapping/adjacent ranges merge; gaps survive.
 *
 * This makes `event_dates` a derived value again (reversing PR #1521's
 * decoupling) while the agenda keeps its own `meeting_room_dates` column as the
 * source of truth for its date-range pickers.
 *
 * @param accommodations - Canonical normalized accommodations map (guestrooms).
 * @param meetingRoomDates - Inclusive meeting-space ranges (already validated).
 * @returns Sorted, merged ranges suitable for the `event_dates` jsonb.
 */
export function deriveEventDates(
  accommodations: RoomsByNight,
  meetingRoomDates: Array<{ startDate: string; endDate: string }>
): Array<{ startDate: string; endDate: string }> {
  return mergeRanges([...deriveContiguousRanges(accommodations), ...(meetingRoomDates ?? [])]);
}

/**
 * Given the agenda's meeting-date ranges (parallel ALTERNATIVES for the same
 * program), return the non-primary ones — every range except the
 * chronologically earliest. A flat agenda renders against that earliest
 * ("primary") range; the rest are the alternative dates the same program is
 * offered on. Sorts by `startDate` first, since stored `meeting_room_dates`
 * order isn't guaranteed chronological.
 *
 * @param ranges - Meeting-date ranges in any order.
 * @returns The non-primary ranges, sorted by start date; empty for 0 or 1 ranges.
 */
export function alternativeMeetingRanges(
  ranges: Array<{ startDate: string; endDate: string }>
): Array<{ startDate: string; endDate: string }> {
  return [...ranges].sort((a, b) => a.startDate.localeCompare(b.startDate)).slice(1);
}

// `formatRoomCount`/`parseRoomCount` moved to `@/lib/utils/room-count` — no longer
// rooms-feature-specific, now also consumed by the shared `SingleColumnDateGrid`.
export { formatRoomCount, parseRoomCount } from "@/lib/utils/room-count";
